/* Copyright (c) 2020-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ReactNode } from "react";
import queryString from "query-string";
import CopyToClipboard from "react-copy-to-clipboard";
import { SVGIcon, THEME_OPTIONS, FlexContainer } from "@nordcloud/gnui";
import { SUCCESS_TEXT } from "~/constants";
import { useCustomer } from "~/context";
import { useGNUITheme } from "~/hooks/useGNUITheme";
import { ROUTES } from "~/routing/routes";
import { useAuth0 } from "~/services";
import { showInfo } from "~/services/toast";
import {
  ArrowWrap,
  BoxWrap,
  DropBtn,
  DropdownWrapStyle,
  DropItem,
  DropLink,
  DropList,
  IconWrap,
  ShowDropDown,
  Username,
} from "./styles";

function UserDropdown() {
  const { logout, user } = useAuth0();
  const { setTheme, isLight } = useGNUITheme();
  const { customer } = useCustomer();

  const userName = user?.name ?? "";
  const nextTheme = isLight ? THEME_OPTIONS.DARK : THEME_OPTIONS.LIGHT;

  const iconTheme = isLight ? "moon" : "sun";

  const getCurrentUrlWithClientId = () => {
    const { origin, pathname, search, hash } = window.location;
    const currentCustomerId = customer?.id ?? "";

    // destructuring to "pop" the customerId out if it exists
    const { customerId: customerIdIgnored, ...otherQueryParams } =
      queryString.parse(search);

    const newQueryParams = {
      ...otherQueryParams,
      customerId: currentCustomerId,
    };

    const newSearchString = queryString.stringify(newQueryParams, {
      arrayFormat: "index",
      sort: false,
    });

    const searchWithQuestionMark = newSearchString ? `?${newSearchString}` : "";

    return `${origin}${pathname}${searchWithQuestionMark}${hash}`;
  };

  return (
    <DropList>
      <Username>{userName}</Username>
      <DropItem>
        <CopyToClipboard
          text={getCurrentUrlWithClientId()}
          onCopy={() => {
            showInfo(SUCCESS_TEXT.copied);
          }}
        >
          <DropBtn>
            <IconWrap>
              <SVGIcon name="copy" />
            </IconWrap>
            Copy link to current view
          </DropBtn>
        </CopyToClipboard>
      </DropItem>
      <DropItem>
        <DropBtn
          onClick={() => {
            window.open(
              "https://docs.fleetcontrol.nordcloudapp.com/docs/release-notes",
              "_blank",
              "noreferrer"
            );
          }}
        >
          <IconWrap>
            <SVGIcon name="mailInformation" />
          </IconWrap>
          Release notes
        </DropBtn>
      </DropItem>
      <DropItem>
        <DropBtn
          onClick={() => {
            window.open(
              `https://docs.fleetcontrol.nordcloudapp.com/docs/intro`,
              "_blank",
              "noreferrer"
            );
          }}
        >
          <IconWrap>
            <SVGIcon name="listView" />
          </IconWrap>
          Documentation
        </DropBtn>
      </DropItem>
      <DropItem>
        <DropLink to={ROUTES.api}>
          <IconWrap>
            <SVGIcon name="api" />
          </IconWrap>
          API Playground
        </DropLink>
      </DropItem>
      <DropItem>
        <DropBtn onClick={() => setTheme(nextTheme)}>
          <IconWrap>
            <SVGIcon name={iconTheme} />
          </IconWrap>
          Switch to {THEME_OPTIONS[nextTheme].toLowerCase()} mode
        </DropBtn>
      </DropItem>
      <DropItem>
        <DropBtn
          onClick={() => {
            logout({
              returnTo: window.location.origin,
            });
          }}
        >
          <IconWrap>
            <SVGIcon name="logout" />
          </IconWrap>
          Log out
        </DropBtn>
      </DropItem>
    </DropList>
  );
}

type Props = {
  children?: ReactNode;
};

export function DropdownWrap({ children }: Props) {
  return (
    <DropdownWrapStyle>
      <BoxWrap>
        <FlexContainer>
          {children}
          <ArrowWrap>
            <SVGIcon size="sm" name="down" />
          </ArrowWrap>
        </FlexContainer>
        <ShowDropDown>
          <UserDropdown />
        </ShowDropDown>
      </BoxWrap>
    </DropdownWrapStyle>
  );
}
